import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { getJbpBridgesCrud } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/commercialBridges/components/businessBridges/helpers/getJbpBridgesCrud'
import { getBusinessBridgeCrud } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/commercialBridges/cruds/getBusinessBridgeCrud'
import { requestRecalculateCompleteness } from 'legacy/lut/features/partner360/completeness'
import { useCallback, useEffect, useRef } from 'react'

import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import Logger from 'core/utils/Logger'
import { keyInitiativesApi } from 'partner360/entities/keyInitiatives'
import { useRefetchPlanningCockpitData } from 'partner360/features/financialCockpit/model/hooks/useRefetchPlanningCockpitData'

interface ICopyInitiativesSessionOptions {
  show: boolean
  partnerId: string
  programType: ProgramTypes
  currentFiscalYear: number
  onClose: () => void
  onCopied: () => void | Promise<void>
  onDataChanged?: () => void | Promise<void>
}

export const useCopyInitiativesSession = ({
  show,
  partnerId,
  programType,
  currentFiscalYear,
  onClose,
  onCopied,
  onDataChanged,
}: ICopyInitiativesSessionOptions) => {
  const dispatch = useAppDispatch()
  const refetchPlanningCockpitData = useRefetchPlanningCockpitData()
  const wasShown = useRef(false)
  const isSaving = useRef(false)
  const isClosing = useRef(false)
  const pending = useRef({ hasSaved: false, shouldRefreshBridges: false })

  const recordSave = useCallback((shouldRefreshBridges: boolean) => {
    pending.current.hasSaved = true
    pending.current.shouldRefreshBridges ||= shouldRefreshBridges
  }, [])

  const setIsSaving = useCallback((value: boolean) => {
    isSaving.current = value
  }, [])

  const close = useCallback(() => {
    if (isSaving.current || isClosing.current) return
    isClosing.current = true
    onClose()
  }, [onClose])

  useEffect(() => {
    if (show) {
      if (!wasShown.current) {
        isClosing.current = false
        isSaving.current = false
      }
      wasShown.current = true
      return
    }

    if (!wasShown.current) return
    wasShown.current = false
    const effects = pending.current
    // Consume once, after the list has actually closed (not in an unmount cleanup).
    pending.current = { hasSaved: false, shouldRefreshBridges: false }
    if (!effects.hasSaved) return

    const finalize = async () => {
      try {
        // Await the calculation before reloading cockpit. Its fulfilled listener refreshes
        // commitment items/statuses, so do not dispatch the same requests again here.
        await dispatch(
          requestRecalculateCompleteness({ partnerId, fYear: currentFiscalYear })
        ).unwrap()
      } catch (error) {
        Logger.error('Error recalculating completeness after copying Key Initiatives', error)
      }

      try {
        if (effects.shouldRefreshBridges) {
          getBusinessBridgeCrud().removeQueries(true)
          getJbpBridgesCrud().removeQueries(true)
        }
      } catch (error) {
        Logger.error('Error refreshing Bridges after copying Key Initiatives', error)
      }

      try {
        if (onDataChanged) {
          await onDataChanged()
        } else {
          await dispatch(
            keyInitiativesApi.endpoints.getKeyInitiatives.initiate(
              { partnerId, fYear: currentFiscalYear, programType },
              { subscribe: false, forceRefetch: true }
            )
          ).unwrap()
        }
      } finally {
        try {
          await onCopied()
        } finally {
          refetchPlanningCockpitData()
        }
      }
    }

    void finalize().catch((error) =>
      Logger.error('Error refreshing data after copying Key Initiatives', error)
    )
  }, [
    currentFiscalYear,
    dispatch,
    onCopied,
    onDataChanged,
    partnerId,
    programType,
    refetchPlanningCockpitData,
    show,
  ])

  return { recordSave, setIsSaving, close }
}
